// frontend/src/hooks/useGameSync.js
import { useEffect, useRef, useCallback } from 'react';
import { useSocket } from '../context/SocketContext'; // Adjust path
import { useGameStore } from '../stores/gameStore'; // Adjust path
import { GAME_STATES, SOCKET_EVENTS } from '../utils/constants'; // Adjust path

export function useGameSync() {
	// --- State Selectors (Only what's needed for effect dependencies) ---
	const gameState = useGameStore((state) => state.gameState);
	const timeLeft = useGameStore((state) => state.timeLeft);
	const countdown = useGameStore((state) => state.countdown);

	// --- Get Specific Actions (Stable References - Needed by effects) ---
	// Get actions needed by the timer effects
	const setTimeLeft = useGameStore((state) => state.setTimeLeft);
	const setCountdown = useGameStore((state) => state.setCountdown);

	const { socket } = useSocket();

	// Refs for interval IDs
	const questionTimerRef = useRef(null);
	const countdownTimerRef = useRef(null);

	// --- Timer Cleanup Utilities (Stable References) ---
	const clearQuestionInterval = useCallback(() => {
		if (questionTimerRef.current) {
			clearInterval(questionTimerRef.current);
			questionTimerRef.current = null;
		}
	}, []);

	const clearCountdownInterval = useCallback(() => {
		if (countdownTimerRef.current) {
			clearInterval(countdownTimerRef.current);
			countdownTimerRef.current = null;
		}
	}, []);

	// --- Effect for Socket Connection & Listeners ---
	useEffect(() => {
		// Only run if the socket instance exists
		if (!socket) {
			console.log('Socket instance not available for listeners.');
			return;
		}

		console.log('Setting up socket listeners...');

		const storeActions = useGameStore.getState();

		// Define handlers INSIDE the effect so they capture the correct 'socket' instance.
		// These handlers call the stable actions obtained from getState().
		const onConnectHandler = ({ playerId }) => {
			localStorage.setItem('triviaPlayerId', playerId);
			storeActions.setError(null);
			console.log('Socket connected:', socket.id);
			storeActions.setSocketId(socket.id);
		};
		const onDisconnectHandler = (reason) => {
			console.log('Socket disconnected:', reason);
			storeActions.setError(`Disconnected: ${reason}`);
			storeActions.setSocketId(null);
		};
		// Wrap store actions if they need extra logic or parameters here
		const onErrorHandler = (payload) => storeActions.handleError(payload);
		const onGameCreatedHandler = (payload) => storeActions.handleGameCreated(payload);
		const onQuestionsLoadedHandler = (payload) => storeActions.handleQuestionsLoaded(payload);
		const onJoinedGameHandler = (payload) => storeActions.handleJoinedGame(payload);
		const onPlayerUpdateHandler = (payload) => storeActions.handlePlayerUpdate(payload);
		const onHostChangedHandler = (payload) => storeActions.handleHostChanged(payload);
		const onGameStartInitiatedHandler = (payload) => storeActions.handleGameStartInitiated(payload);
		const onQuestionCountdownHandler = (payload) => storeActions.handleQuestionCountdown(payload);
		const onResultsCountdownHandler = (payload) => storeActions.handleResultsCountdown(payload);
		const onNewQuestionHandler = (payload) => storeActions.handleNewQuestion(payload);
		const onAnswerResultHandler = (payload) => storeActions.handleAnswerResult(payload);
		const onRoundResultsHandler = (payload) => storeActions.handleRoundResults(payload);
		const onGameResultsHandler = (payload) => storeActions.handleGameResults(payload);
		const onGameEndedHandler = (payload) => storeActions.handleGameEndedExternally(payload);
		const onGameStateSyncHandler = (payload) => storeActions.handleGameStateSync(payload);
		const onGameResetHandler = () => storeActions.resetGame();

		// Register listeners using the defined handlers
		socket.on(SOCKET_EVENTS.CONNECT, onConnectHandler);
		socket.on(SOCKET_EVENTS.DISCONNECT, onDisconnectHandler);
		socket.on(SOCKET_EVENTS.ERROR, onErrorHandler);
		socket.on(SOCKET_EVENTS.GAME_CREATED, onGameCreatedHandler);
		socket.on(SOCKET_EVENTS.QUESTIONS_LOADED, onQuestionsLoadedHandler);
		socket.on(SOCKET_EVENTS.JOINED_GAME, onJoinedGameHandler);
		socket.on(SOCKET_EVENTS.PLAYER_UPDATE, onPlayerUpdateHandler);
		socket.on(SOCKET_EVENTS.HOST_CHANGED, onHostChangedHandler);
		socket.on(SOCKET_EVENTS.GAME_START_INITIATED, onGameStartInitiatedHandler);
		socket.on(SOCKET_EVENTS.QUESTION_COUNTDOWN, onQuestionCountdownHandler);
		socket.on(SOCKET_EVENTS.RESULTS_COUNTDOWN, onResultsCountdownHandler);
		socket.on(SOCKET_EVENTS.NEW_QUESTION, onNewQuestionHandler);
		socket.on(SOCKET_EVENTS.ANSWER_RESULT, onAnswerResultHandler);
		socket.on(SOCKET_EVENTS.ROUND_RESULTS, onRoundResultsHandler);
		socket.on(SOCKET_EVENTS.GAME_RESULTS, onGameResultsHandler);
		socket.on(SOCKET_EVENTS.GAME_ENDED, onGameEndedHandler);
		socket.on(SOCKET_EVENTS.GAME_RESET, onGameResetHandler);
		socket.on(SOCKET_EVENTS.GAME_STATE_SYNC, onGameStateSyncHandler);

		// Clean up all listeners using the same handler references
		return () => {
			console.log('Cleaning up socket listeners...');
			socket.off(SOCKET_EVENTS.CONNECT, onConnectHandler);
			socket.off(SOCKET_EVENTS.DISCONNECT, onDisconnectHandler);
			socket.off(SOCKET_EVENTS.ERROR, onErrorHandler);
			socket.off(SOCKET_EVENTS.GAME_CREATED, onGameCreatedHandler);
			socket.off(SOCKET_EVENTS.JOINED_GAME, onJoinedGameHandler);
			socket.off(SOCKET_EVENTS.PLAYER_UPDATE, onPlayerUpdateHandler);
			socket.off(SOCKET_EVENTS.HOST_CHANGED, onHostChangedHandler);
			socket.off(SOCKET_EVENTS.GAME_START_INITIATED, onGameStartInitiatedHandler);
			socket.off(SOCKET_EVENTS.QUESTION_COUNTDOWN, onQuestionCountdownHandler);
			socket.off(SOCKET_EVENTS.RESULTS_COUNTDOWN, onResultsCountdownHandler);
			socket.off(SOCKET_EVENTS.NEW_QUESTION, onNewQuestionHandler);
			socket.off(SOCKET_EVENTS.ANSWER_RESULT, onAnswerResultHandler);
			socket.off(SOCKET_EVENTS.ROUND_RESULTS, onRoundResultsHandler);
			socket.off(SOCKET_EVENTS.GAME_RESULTS, onGameResultsHandler);
			socket.off(SOCKET_EVENTS.GAME_ENDED, onGameEndedHandler);
			socket.off(SOCKET_EVENTS.GAME_STATE_SYNC, onGameStateSyncHandler);
			socket.off(SOCKET_EVENTS.GAME_RESET, onGameResetHandler);
		};
	}, [socket]);

	// --- Effect for Question Timer ---
	useEffect(() => {
		if (gameState === GAME_STATES.PLAYING && timeLeft > 0) {
			if (questionTimerRef.current) clearQuestionInterval();
			questionTimerRef.current = setInterval(() => {
				setTimeLeft(timeLeft - 1);
			}, 1000);
		} else {
			clearQuestionInterval();
		}
		// Cleanup function
		return () => clearQuestionInterval();
	}, [gameState, timeLeft, setTimeLeft]);

	// --- Effect for Countdown Timer ---
	useEffect(() => {
		if (typeof countdown === 'number' && countdown > 0) {
			if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
			countdownTimerRef.current = setInterval(() => {
				setCountdown(countdown - 1);
			}, 1000);
		} else if (countdown === 0) {
			// Ensure timer is cleared and state is updated when countdown hits zero
			clearInterval(countdownTimerRef.current);
			setCountdown(null);
		} else {
			// Clear interval if countdown is null or invalid
			clearCountdownInterval();
		}
		// Cleanup function
		return () => clearCountdownInterval();
	}, [countdown, setCountdown]);
}
